import { useState } from 'react'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'
import { useNavigate } from 'react-router-dom'
import api from '../lib/api'

export default function LaneNewPage() {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [customerName, setCustomerName] = useState('')
  const [originCity, setOriginCity] = useState('')
  const [originState, setOriginState] = useState('')
  const [originZip, setOriginZip] = useState('')
  const [destinationCity, setDestinationCity] = useState('')
  const [destinationState, setDestinationState] = useState('')
  const [destinationZip, setDestinationZip] = useState('')
  const [estDistanceMiles, setEstDistanceMiles] = useState('')
  const [transportType, setTransportType] = useState('OPEN')
  const [targetCustomerPay, setTargetCustomerPay] = useState('')
  const [targetCarrierPay, setTargetCarrierPay] = useState('')
  const [notes, setNotes] = useState('')
  const originZipLookup = useZipLocation({ zip: originZip, onCity: setOriginCity, onState: setOriginState, lookupZip: api.geo.zipLookup })
  const destinationZipLookup = useZipLocation({ zip: destinationZip, onCity: setDestinationCity, onState: setDestinationState, lookupZip: api.geo.zipLookup })

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!originZip.trim() || !destinationZip.trim()) {
      setError('Origin and destination ZIP are required')
      return
    }

    setSaving(true)
    try {
      const response = await api.lanes.create({
        customerName: customerName || undefined,
        originCity: originCity || undefined,
        originState: originState || undefined,
        originZip: originZip || undefined,
        destinationCity: destinationCity || undefined,
        destinationState: destinationState || undefined,
        destinationZip: destinationZip || undefined,
        estDistanceMiles: estDistanceMiles ? Number(estDistanceMiles) : undefined,
        transportType,
        targetCustomerPay: targetCustomerPay ? Number(targetCustomerPay) : undefined,
        targetCarrierPay: targetCarrierPay ? Number(targetCarrierPay) : undefined,
        notes: notes || undefined,
      })
      navigate(`/lanes/${response.data.lane.id}`)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to create lane')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-bold">New Shipping Lane</h1>
      {error && <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

      <form className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={submit}>
        <input className="border rounded px-3 py-2 text-sm md:col-span-2" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Customer name" />

        <input className="border rounded px-3 py-2 text-sm" value={originCity} onChange={(e) => setOriginCity(e.target.value)} placeholder="Origin city" />
        <input className="border rounded px-3 py-2 text-sm" value={originState} onChange={(e) => setOriginState(e.target.value)} placeholder="Origin state" />
        <input className="border rounded px-3 py-2 text-sm" value={originZip} onChange={(e) => setOriginZip(normalizeZipInput(e.target.value))} placeholder="Origin ZIP" maxLength={10} inputMode="numeric" />

        <input className="border rounded px-3 py-2 text-sm" value={destinationCity} onChange={(e) => setDestinationCity(e.target.value)} placeholder="Destination city" />
        <input className="border rounded px-3 py-2 text-sm" value={destinationState} onChange={(e) => setDestinationState(e.target.value)} placeholder="Destination state" />
        <input className="border rounded px-3 py-2 text-sm" value={destinationZip} onChange={(e) => setDestinationZip(normalizeZipInput(e.target.value))} placeholder="Destination ZIP" maxLength={10} inputMode="numeric" />
        {(originZipLookup.loading || destinationZipLookup.loading || originZipLookup.message || destinationZipLookup.message) && <p className="text-xs text-slate-500" role="status">{originZipLookup.loading || destinationZipLookup.loading ? 'Looking up ZIP...' : originZipLookup.message ?? destinationZipLookup.message}</p>}

        <input className="border rounded px-3 py-2 text-sm" value={estDistanceMiles} onChange={(e) => setEstDistanceMiles(e.target.value)} placeholder="Est. distance miles" />
        <select className="border rounded px-3 py-2 text-sm" value={transportType} onChange={(e) => setTransportType(e.target.value)}>
          <option value="OPEN">OPEN</option>
          <option value="ENCLOSED">ENCLOSED</option>
        </select>

        <input className="border rounded px-3 py-2 text-sm" value={targetCustomerPay} onChange={(e) => setTargetCustomerPay(e.target.value)} placeholder="Target customer pay ($)" />
        <input className="border rounded px-3 py-2 text-sm" value={targetCarrierPay} onChange={(e) => setTargetCarrierPay(e.target.value)} placeholder="Target carrier pay ($)" />

        <textarea className="border rounded px-3 py-2 text-sm md:col-span-2" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Lane notes" />

        <div className="md:col-span-2 flex gap-2">
          <button disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded text-sm">{saving ? 'Saving...' : 'Create Lane'}</button>
          <button type="button" onClick={() => navigate('/lanes')} className="border px-4 py-2 rounded text-sm">Cancel</button>
        </div>
      </form>
    </div>
  )
}
